'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import styles from './CategoriesV2.module.css';

const categoryTranslations = {
  'Beds & Furniture': {
    es: 'Camas y Muebles',
    de: 'Betten & Möbel',
    fr: 'Lits & Meubles',
    en: 'Beds & Furniture',
  },
  'Clothing & Accessories': {
    es: 'Ropa y Accesorios',
    de: 'Kleidung & Zubehör',
    fr: 'Vêtements & Accessoires',
    en: 'Clothing & Accessories',
  },
  'Collars, Harnesses & Leashes': {
    es: 'Collares, Arneses y Correas',
    de: 'Halsbänder, Geschirre & Leinen',
    fr: 'Colliers, Harnais & Laisses',
    en: 'Collars, Harnesses & Leashes',
  },
  'Food': {
    es: 'Alimentación',
    de: 'Futter',
    fr: 'Alimentation',
    en: 'Food',
  },
  'Health': {
    es: 'Salud',
    de: 'Gesundheit',
    fr: 'Santé',
    en: 'Health',
  },
  'Hygiene': {
    es: 'Higiene',
    de: 'Hygiene',
    fr: 'Hygiène',
    en: 'Hygiene',
  },
  'Kennels & Crates': {
    es: 'Casetas y Transportines',
    de: 'Hundehütten & Transportboxen',
    fr: 'Niches & Caisses',
    en: 'Kennels & Crates',
  },
  'Toys': {
    es: 'Juguetes',
    de: 'Spielzeug',
    fr: 'Jouets',
    en: 'Toys',
  },
  'Training': {
    es: 'Adiestramiento',
    de: 'Training',
    fr: 'Dressage',
    en: 'Training',
  },
};

const productsLabel = {
  es: 'productos',
  de: 'Produkte',
  fr: 'produits',
  en: 'products',
};

function getCategoryName(englishName, locale) {
  const translations = categoryTranslations[englishName];
  if (translations && translations[locale]) return translations[locale];
  return englishName;
}

export default function CategoriesV2({ locale = 'en' }) {
  const t = useTranslations('home');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    async function fetchCategories() {
      try {
        const res = await fetch('/api/bigbuy/products?perPage=500', { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          const products = data.data || [];

          // Group by category, pick a representative image for each
          const catMap = {};
          products.forEach(p => {
            const cat = p.category || 'Other';
            if (!catMap[cat]) {
              catMap[cat] = { name: cat, count: 0, image: null };
            }
            catMap[cat].count++;
            if (!catMap[cat].image && p.image && !p.image.includes('placeholder')) {
              catMap[cat].image = p.image;
            }
          });

          const sorted = Object.values(catMap).sort((a, b) => b.count - a.count);
          setCategories(sorted);
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    }
    fetchCategories();
  }, []);

  if (categories.length === 0) return null;

  const label = productsLabel[locale] || productsLabel.en;

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <h2 className={styles.title}>{t('shopByCategory')}</h2>

        <div className={styles.grid}>
          {categories.map(cat => (
            <Link
              key={cat.name}
              href={`/${locale}/productos`}
              className={styles.card}
            >
              {cat.image ? (
                <div className={styles.imageWrapper}>
                  <img src={cat.image} alt={getCategoryName(cat.name, locale)} className={styles.catImage} loading="lazy" />
                </div>
              ) : (
                <div className={styles.icon}>📦</div>
              )}
              <div className={styles.name}>{getCategoryName(cat.name, locale)}</div>
              <div className={styles.catCount}>{cat.count} {label}</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
